import React, { useEffect, useRef, useState, useCallback } from 'react';
import { router } from '@inertiajs/react';
import Layout from '../components/Layout.jsx';
import { getCableConsumer } from '../cable.js';
import AttendanceRecorder from './WebinarRoom/AttendanceRecorder.jsx';
import useAttendanceEvents from './WebinarRoom/useAttendanceEvents.js';
import { getCsrfToken } from './WebinarRoom/utils.js';
import { mergeChatMessage } from '../lib/chat_messages.js';

// Vendored hls.js — served from public/vendor. To bump: replace the file and
// update the version in the filename here and in BroadcastRecording.jsx.
const HLS_JS_SRC = '/vendor/hls-1.5.13.min.js';

let hlsLoaderPromise = null;
function loadHlsJs() {
  if (window.Hls) return Promise.resolve(window.Hls);
  if (hlsLoaderPromise) return hlsLoaderPromise;
  hlsLoaderPromise = new Promise((resolve, reject) => {
    const tag = document.createElement('script');
    tag.src = HLS_JS_SRC;
    tag.async = true;
    tag.onload = () => resolve(window.Hls);
    tag.onerror = () => reject(new Error('Failed to load hls.js'));
    document.head.appendChild(tag);
  });
  return hlsLoaderPromise;
}

function appendToken(url, token) {
  if (!url) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}token=${encodeURIComponent(token)}`;
}

// Public STUN — the LAN/STUN happy path doesn't need TURN. Cloudflare TURN
// creds (when configured) get prepended so VPN/symmetric-NAT clients still
// connect; STUN stays as a fallback in that ICE config too.
const STUN_FALLBACK = [{ urls: 'stun:stun.l.google.com:19302' }];

async function fetchIceServers(endpoint) {
  if (!endpoint) return STUN_FALLBACK;
  try {
    const res = await fetch(endpoint, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': getCsrfToken(),
        Accept: 'application/json',
      },
    });
    if (!res.ok) return STUN_FALLBACK;
    const data = await res.json();
    const servers = Array.isArray(data?.ice_servers) ? data.ice_servers : [];
    return servers.length > 0 ? [...servers, ...STUN_FALLBACK] : STUN_FALLBACK;
  } catch (_) {
    return STUN_FALLBACK;
  }
}

// Minimal WHEP (WebRTC-HTTP Egress Protocol) client — audio + video recvonly.
// Spec: https://datatracker.ietf.org/doc/draft-murillo-whep/
// Negotiates a WebRTC session against MediaMTX's <path>/whep endpoint and
// pipes the incoming MediaStream into the supplied <video>. Returns an
// object with stop() that DELETEs the WHEP resource and closes the PC.
async function startWhep({ url, video, iceServers, onStateChange }) {
  const pc = new RTCPeerConnection({ iceServers });
  // Offer to receive both audio and the instructor camera video. Audio-only
  // webinars simply never fill the video transceiver — the <video> element
  // stays 0×0 until a real video track arrives (see hasVideo in the body).
  pc.addTransceiver('audio', { direction: 'recvonly' });
  pc.addTransceiver('video', { direction: 'recvonly' });

  const remoteStream = new MediaStream();
  pc.ontrack = (e) => {
    (e.streams[0]?.getTracks() || [e.track]).forEach((t) => {
      if (!remoteStream.getTracks().includes(t)) remoteStream.addTrack(t);
    });
    if (video.srcObject !== remoteStream) video.srcObject = remoteStream;
  };
  pc.oniceconnectionstatechange = () => onStateChange?.(pc.iceConnectionState);

  // Everything from the offer onward can throw — a rejected POST, a network
  // drop, an unparseable answer SDP. Each failure has to close the peer
  // connection or it leaks: the startup ladder retries several times before
  // conceding to HLS, so an unclosed PC per attempt would accumulate.
  try {
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    // Same trickle-less approach as the WHIP publisher — wait for gathering
    // to complete (or a short timeout) so the offer SDP carries candidates.
    await new Promise((resolve) => {
      if (pc.iceGatheringState === 'complete') return resolve();
      const t = setTimeout(resolve, 1500);
      pc.addEventListener('icegatheringstatechange', () => {
        if (pc.iceGatheringState === 'complete') { clearTimeout(t); resolve(); }
      });
    });

    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/sdp' },
      body: pc.localDescription.sdp,
    });
    if (!res.ok) throw new Error(`WHEP server rejected the offer (HTTP ${res.status}).`);

    const answerSdp = await res.text();
    await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

    const resourceUrl = res.headers.get('Location');
    return whepSession(pc, url, resourceUrl);
  } catch (e) {
    // Drop the handlers first so a dying connection's final state change can't
    // re-enter the caller's retry logic on a session it never received.
    pc.ontrack = null;
    pc.oniceconnectionstatechange = null;
    pc.close();
    throw e;
  }
}

// Statuses that mean "the session is already gone", which is the NORMAL result
// of Stop Broadcast: MediaMTX reaps the WHEP reader the moment the publisher
// detaches, so our explicit DELETE arrives after the session no longer exists.
// That is successful cleanup, not a failure.
const WHEP_ALREADY_GONE_STATUSES = [ 404, 410 ];

// ~6s of playlist warm-up at 1.2s apart. Past that the muxer is not coming up
// on its own and WHEP owns recovery.
//
// Module scope because two paths spend this one budget: hls.js's fatal-error
// handler and the safety-net interval below. Sharing the counter is what keeps
// them one retry system rather than two independent ones — which matters most
// on the native path (Safari/iOS), where there is no hls.js error handler.
const HLS_MAX_NETWORK_RETRIES = 5;

// MediaError codes that mean a native <video> has rejected the SOURCE itself,
// so re-assigning the same URL cannot help. MEDIA_ERR_NETWORK (2) is
// deliberately absent: a 404 while the muxer warms up looks exactly like that,
// and it deserves the bounded retry budget instead of an immediate stop.
const MEDIA_ERR_DECODE = 3;
const MEDIA_ERR_SRC_NOT_SUPPORTED = 4;
const NATIVE_HLS_FATAL_MEDIA_ERRORS = [ MEDIA_ERR_DECODE, MEDIA_ERR_SRC_NOT_SUPPORTED ];

function whepSession(pc, url, resourceUrl) {
  // At most one DELETE per session, no matter how many callers race to stop it
  // (the broadcast_state 'ended' branch, connectWhep replacing a session, and
  // the effect cleanup can all reach here).
  let stopped = false;

  return {
    pc,
    stop: async () => {
      // Closing the local peer connection must never depend on the DELETE, so
      // it happens first and unconditionally.
      if (stopped) { pc.close(); return; }
      stopped = true;
      try {
        if (resourceUrl) {
          const absolute = new URL(resourceUrl, url).toString();
          const res = await fetch(absolute, { method: 'DELETE' });
          if (!res.ok && !WHEP_ALREADY_GONE_STATUSES.includes(res.status)) {
            // A genuine teardown failure stays diagnosable — status only, never
            // the signed URL. No retry: the session is unreachable either way
            // and the local peer connection is closed below regardless.
            // eslint-disable-next-line no-console
            console.warn(`[WebinarRoom] WHEP session teardown returned HTTP ${res.status}`);
          }
        }
      } catch (_) { /* network died with the page — local close still runs */ }
      pc.close();
    },
  };
}

function fmtClock(d) {
  if (!d) return '';
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

// Viewer-side slide display. Composes the visible pages array from the deck
// (rasterized PDF pages) + optional bio slide as page 0 — the bio flag is
// server-provided and can flip mid-broadcast without a re-fetch. Parent owns
// the section state and listens on WebinarPresentationChannel; this component
// just renders whatever deck+page it's told to show.
function SlideViewer({ section, onSlideChange, isNarrow }) {
  const deck = section?.slide_deck;
  const bioPage = section?.show_bio_slide ? section?.bio_page : null;
  // Compose: bio (if enabled) + deck pages. Never mutate the props.
  const pages = React.useMemo(() => {
    const list = [];
    if (bioPage) list.push(bioPage);
    (deck?.pages || []).forEach((p) => list.push(p));
    return list;
  }, [bioPage, deck?.pages]);
  const minPage = pages[0]?.page ?? 1;
  const maxPage = pages[pages.length - 1]?.page ?? 1;
  const requested = section?.current_slide_page;
  const currentPage = Math.max(minPage, Math.min(requested ?? minPage, maxPage));

  const deckAttached = deck?.attached || !!bioPage;
  const deckReady = deck?.attached ? deck.ready : !!bioPage;
  const currentImage = pages.find((p) => p.page === currentPage) || pages[0];
  const nextImage = pages.find((p) => p.page === currentPage + 1);
  const isBioSlide = currentPage === 0;
  const labelIndex = isBioSlide ? 'Bio' : currentPage;
  const labelTotal = minPage === 0 ? maxPage : pages.length;
  const canRenderSlide = deckAttached && deckReady && !!currentImage;

  const [expanded, setExpanded] = useState(false);
  const expandButtonRef = useRef(null);
  const closeButtonRef = useRef(null);
  const wasExpandedRef = useRef(false);

  useEffect(() => {
    onSlideChange?.(currentPage);
  }, [currentPage, onSlideChange]);

  // Losing the slide would otherwise strand the overlay: it renders nothing, so
  // the Close button disappears while the scroll lock stays installed.
  useEffect(() => {
    if (expanded && !canRenderSlide) setExpanded(false);
  }, [expanded, canRenderSlide]);

  // A full-viewport overlay rather than the Fullscreen API, which iOS Safari
  // does not support on non-video elements. `overflow: hidden` alone does not
  // hold on mobile Safari, so the body is pinned at the negative scroll offset.
  // Every inline value touched is captured and restored verbatim.
  useEffect(() => {
    if (!expanded) return undefined;
    const body = document.body;
    const saved = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
    };
    const scrollY = window.scrollY;
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';

    const onKey = (e) => { if (e.key === 'Escape') setExpanded(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      body.style.overflow = saved.overflow;
      body.style.position = saved.position;
      body.style.top = saved.top;
      body.style.width = saved.width;
      window.scrollTo(0, scrollY);
    };
  }, [expanded]);

  useEffect(() => {
    if (expanded) {
      wasExpandedRef.current = true;
      closeButtonRef.current?.focus();
    } else if (wasExpandedRef.current) {
      wasExpandedRef.current = false;
      expandButtonRef.current?.focus();
    }
  }, [expanded]);

  if (!deckAttached) return null;
  if (!deckReady) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6 text-center">
        <p className="text-sm text-gray-600">The presenter is preparing the slide deck…</p>
      </div>
    );
  }

  return (
    <>
      <div
        data-testid="slide-card"
        className="rounded-2xl overflow-hidden border border-gray-200 bg-gray-900 shadow-lg"
      >
        <div className="relative w-full flex items-center justify-center" style={{ aspectRatio: '4 / 3' }}>
          {currentImage && (
            <img
              src={currentImage.url}
              alt={isBioSlide ? 'Instructor bio slide' : `Slide ${currentPage}`}
              className="max-h-full max-w-full object-contain"
              draggable={false}
            />
          )}
          <div className="absolute bottom-2 right-3 px-2 py-0.5 rounded-full bg-black/60 text-white text-xs font-mono tabular-nums">
            {labelIndex}{isBioSlide ? '' : ` / ${labelTotal}`}
          </div>
          {isNarrow && canRenderSlide && (
            <button
              ref={expandButtonRef}
              type="button"
              data-testid="slide-expand"
              onClick={() => setExpanded(true)}
              aria-label="Expand slide to full screen"
              className="absolute bottom-2 left-3 min-h-11 min-w-11 inline-flex items-center justify-center rounded-lg bg-black/60 px-3 text-xs font-semibold text-white hover:bg-black/75 focus:outline-none focus-visible:ring-2 focus-visible:ring-white cursor-pointer"
            >
              Expand
            </button>
          )}
        </div>
        {nextImage && (
          <img src={nextImage.url} alt="" aria-hidden="true" className="hidden" />
        )}
      </div>

      {/* Renders from the same currentImage as the card, so a host slide change
          lands here too rather than in a second stale copy. */}
      {expanded && canRenderSlide && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Expanded slide"
          data-testid="slide-expanded"
          className="fixed inset-0 flex flex-col bg-black"
          style={{
            zIndex: 200,
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)',
          }}
        >
          <div className="flex items-center justify-between gap-3 px-3 py-2 text-white">
            <span data-testid="slide-expanded-count" className="text-xs font-mono tabular-nums text-white/80">
              {labelIndex}{isBioSlide ? '' : ` / ${labelTotal}`}
            </span>
            <button
              ref={closeButtonRef}
              type="button"
              data-testid="slide-expanded-close"
              onClick={() => setExpanded(false)}
              aria-label="Close expanded slide"
              className="min-h-11 min-w-11 inline-flex items-center justify-center rounded-lg px-3 text-sm font-semibold text-white hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white cursor-pointer"
            >
              Close
            </button>
          </div>
          <div data-testid="slide-expanded-stage" className="flex-1 min-h-0 flex items-center justify-center px-2 pb-2">
            <img
              data-testid="slide-expanded-image"
              src={currentImage.url}
              alt={isBioSlide ? 'Instructor bio slide' : `Slide ${currentPage}`}
              className="max-h-full max-w-full object-contain"
              draggable={false}
            />
          </div>
        </div>
      )}
    </>
  );
}


function fmtTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

// Viewer-side chat — a single private thread with the host. Subscribes to its
// own viewer stream, so other viewers' messages are never delivered to it.
function ViewerChat({ webinarId, chat }) {
  const [messages, setMessages] = useState(() => chat?.messages || []);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (!webinarId) return;
    const consumer = getCableConsumer();
    const sub = consumer.subscriptions.create(
      { channel: 'WebinarChatChannel', webinar_id: webinarId },
      {
        received: (data) => {
          setMessages((prev) => mergeChatMessage(prev, data));
        },
      },
    );
    return () => sub.unsubscribe();
  }, [webinarId]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const send = useCallback(async (e) => {
    e?.preventDefault?.();
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    setError(null);
    try {
      const res = await fetch(chat.post_endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
          Accept: 'application/json',
        },
        body: JSON.stringify({ body }),
      });
      if (!res.ok) throw new Error('Could not send message.');
      // Show the sender their own message immediately rather than waiting on
      // the echo, which may be delayed or never arrive. A malformed 2xx must
      // reach the error handler rather than clear the draft into nothing.
      const message = await res.json();
      if (!message?.id) throw new Error('Could not send message.');
      setMessages((prev) => mergeChatMessage(prev, message));
      setDraft('');
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  }, [draft, sending, chat?.post_endpoint]);

  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <p className="text-sm font-semibold text-gray-800">Webinar chat</p>
        <span className="text-xs text-gray-500">Everyone in this webinar can see these messages</span>
      </div>

      <div ref={scrollerRef} data-testid="chat-messages" className="h-72 overflow-y-auto px-4 py-3 space-y-2 bg-gray-50">
        {messages.length === 0 ? (
          <p className="text-xs text-gray-500 text-center pt-8">No messages yet. Say hello!</p>
        ) : (
          messages.map((m) => {
            const fromHost = m.sender_role === 'host';
            return (
              <div key={m.id} data-testid="chat-message" className="flex justify-start">
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
                  fromHost ? 'bg-indigo-50 border border-indigo-200 text-gray-900' : 'bg-white border border-gray-200 text-gray-800'
                }`}>
                  <p
                    data-testid="chat-sender"
                    className={`text-[10px] font-semibold uppercase tracking-wide mb-0.5 ${
                      fromHost ? 'text-indigo-700' : 'text-gray-500'
                    }`}
                  >
                    {fromHost ? 'Host' : m.sender_name}
                  </p>
                  <p className="whitespace-pre-wrap break-words">{m.body}</p>
                  <p className="text-[10px] mt-0.5 tabular-nums text-gray-400">
                    {fmtTime(m.created_at)}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>

      <form onSubmit={send} className="p-3 border-t border-gray-100 flex items-end gap-2">
        <textarea
          rows={1}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
          }}
          placeholder="Message everyone…"
          disabled={sending}
          className="flex-1 resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none disabled:bg-gray-50"
        />
        <button
          type="submit"
          disabled={sending || !draft.trim()}
          className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
        >
          Send
        </button>
      </form>
      {error && <p className="px-4 pb-3 text-xs text-rose-600">{error}</p>}
    </div>
  );
}

// Custom controls for the live audio stream. The native <video controls> bar
// renders a seek timeline and a "current / duration" timer, both of which
// flicker on a live LL-HLS stream because `duration` grows by ~1s every second
// as new parts arrive. Live audio also can't be seeked. Render a minimal
// play/mute/volume toolbar instead and drive the underlying <video> via ref.
function LiveAudioControls({ videoRef }) {
  const [paused, setPaused] = useState(true);
  const [muted, setMuted] = useState(true);
  const [volume, setVolume] = useState(1);
  const [lastUnmutedVolume, setLastUnmutedVolume] = useState(1);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const sync = () => {
      setPaused(v.paused);
      setMuted(v.muted);
      setVolume(v.volume);
    };
    sync();
    const events = ['play', 'pause', 'volumechange', 'loadedmetadata'];
    events.forEach((e) => v.addEventListener(e, sync));
    return () => events.forEach((e) => v.removeEventListener(e, sync));
  }, [videoRef]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {});
    else v.pause();
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.muted) {
      v.muted = false;
      if (v.volume === 0) v.volume = lastUnmutedVolume || 1;
    } else {
      setLastUnmutedVolume(v.volume || 1);
      v.muted = true;
    }
  };

  const onVolume = (e) => {
    const v = videoRef.current;
    if (!v) return;
    const next = Number(e.target.value);
    v.volume = next;
    if (next > 0) {
      v.muted = false;
      setLastUnmutedVolume(next);
    }
  };

  const effectiveVolume = muted ? 0 : volume;

  return (
    <div className="flex flex-1 items-center gap-3 min-w-0 bg-white/95 rounded-full px-3 py-1.5">
      <button
        type="button"
        onClick={togglePlay}
        aria-label={paused ? 'Play' : 'Pause'}
        className="shrink-0 text-gray-800 hover:text-gray-900 cursor-pointer"
      >
        {paused ? (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
        ) : (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M6 5h4v14H6zM14 5h4v14h-4z" /></svg>
        )}
      </button>
      <button
        type="button"
        onClick={toggleMute}
        aria-label={muted || volume === 0 ? 'Unmute' : 'Mute'}
        className="shrink-0 text-gray-800 hover:text-gray-900 cursor-pointer"
      >
        {muted || volume === 0 ? (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M16.5 12A4.5 4.5 0 0014 7.97v2.21l2.45 2.45a4.6 4.6 0 00.05-.63zM19 12c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.8 8.8 0 0021 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06a8.99 8.99 0 003.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" /></svg>
        ) : (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0014 7.97v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" /></svg>
        )}
      </button>
      <input
        type="range"
        min="0"
        max="1"
        step="0.05"
        value={effectiveVolume}
        onChange={onVolume}
        aria-label="Volume"
        className="flex-1 min-w-0 accent-indigo-600 cursor-pointer"
      />
    </div>
  );
}

function BroadcastBadge({ state }) {
  const cfg = state === 'live'
    ? { label: 'Live', dot: 'bg-rose-500 animate-pulse', cls: 'bg-rose-50 text-rose-700 border-rose-200' }
    : state === 'ended'
      ? { label: 'Ended', dot: 'bg-gray-400', cls: 'bg-gray-100 text-gray-700 border-gray-200' }
      : state === 'reconnecting'
        ? { label: 'Reconnecting', dot: 'bg-amber-400 animate-pulse', cls: 'bg-amber-50 text-amber-800 border-amber-200' }
        : { label: 'Waiting to start', dot: 'bg-amber-400', cls: 'bg-amber-50 text-amber-800 border-amber-200' };
  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold border ${cfg.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

// Polls auto-close after 2 minutes (server-enforced via WebinarPollAutoCloseJob).
// This hook returns remaining seconds for the countdown UI; the server is still
// the source of truth — when it fires the close, every client gets the close
// broadcast and swaps to results.
function usePollCountdown(closesAt) {
  const target = closesAt ? new Date(closesAt).getTime() : null;
  const compute = () => (target ? Math.max(0, Math.ceil((target - Date.now()) / 1000)) : null);
  const [remaining, setRemaining] = useState(compute);
  useEffect(() => {
    if (!target) { setRemaining(null); return; }
    setRemaining(compute());
    const id = setInterval(() => setRemaining(compute()), 1000);
    return () => clearInterval(id);
  }, [target]);
  return remaining;
}

function formatCountdown(seconds) {
  if (seconds == null) return '';
  const clamped = Math.max(0, seconds);
  const m = Math.floor(clamped / 60);
  const s = clamped % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}


// Viewer-side poll widget. Renders the currently open poll (if any) as radio
// buttons; on submit POSTs to the response endpoint and stays in "answered"
// mode until the host closes the poll. When a close event arrives, swaps to
// the aggregate results view with bars + percentages.
function PollViewer({ webinarId, poll, onPollOffered, onAttentionChange, anchorRef }) {
  // One state because closing swaps them: the close payload carries no question
  // or options, so the results card can only be built from the poll being shown
  // at the moment the event lands.
  const [view, setView] = useState(() => ({
    current: poll?.current || null,
    results: poll?.previous ? {
      question: poll.previous.question,
      options: poll.previous.options || [],
      counts: poll.previous.counts || {},
      total: poll.previous.total ?? 0,
      existing_choice_id: poll.previous.existing_choice_id ?? null,
    } : null,
  }));
  const { current, results } = view;
  const [selected, setSelected] = useState(() => poll?.current?.existing_choice_id || null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Report "this poll wants the student's attention" upward so the room can
  // surface a banner — the card itself renders below the presentation and is
  // easy to miss, especially on a phone. Keyed on the poll id, so a duplicate
  // open payload for the same poll is not a new demand for attention. Goes
  // quiet once `existing_choice_id` is set, which only happens after the POST
  // actually succeeded.
  const attentionId = current?.id ?? null;
  const attentionAnswered = current?.existing_choice_id != null;
  useEffect(() => {
    onAttentionChange?.(attentionId ? { id: attentionId, answered: attentionAnswered } : null);
  }, [attentionId, attentionAnswered, onAttentionChange]);
  // Clear on unmount so a stale banner can't outlive the card.
  useEffect(() => () => onAttentionChange?.(null), [onAttentionChange]);

  // Keep `selected` in sync if a new poll opens via the channel.
  useEffect(() => {
    setSelected(current?.existing_choice_id || null);
  }, [current?.id]);

  const refreshedPoll = poll?.current;
  useEffect(() => {
    if (!refreshedPoll?.id || refreshedPoll.existing_choice_id == null) return;
    // Ignore a partial reload that belongs to a previously active poll.
    if (current?.id !== refreshedPoll.id || current.existing_choice_id != null) return;
    setView((v) => (
      v.current && v.current.id === refreshedPoll.id && v.current.existing_choice_id == null
        ? { ...v, current: { ...v.current, existing_choice_id: refreshedPoll.existing_choice_id } }
        : v
    ));
    setSelected((s) => s ?? refreshedPoll.existing_choice_id);
  }, [refreshedPoll?.id, refreshedPoll?.existing_choice_id, current?.id, current?.existing_choice_id]);

  // Attendance signal: every distinct poll the viewer is shown counts toward
  // "polls offered" so the summary can compute an answered/offered ratio.
  useEffect(() => {
    if (current?.id) onPollOffered?.(current.id);
  }, [current?.id, onPollOffered]);

  useEffect(() => {
    if (!webinarId) return;
    const consumer = getCableConsumer();
    const sub = consumer.subscriptions.create(
      { channel: 'WebinarPollChannel', webinar_id: webinarId },
      {
        received: (data) => {
          if (!data?.event) return;
          if (data.event === 'open' && data.poll?.id) {
            setView({
              current: {
                id: data.poll.id,
                question: data.poll.question,
                options: data.poll.options || [],
                existing_choice_id: null,
                closes_at: data.poll.closes_at || null,
              },
              results: null,
            });
            setSelected(null);
            setError(null);
            // Prior response state is viewer-specific.
            if (data.poll.reopened) {
              router.reload({ only: ['poll'], preserveScroll: true });
            }
          } else if (data.event === 'close' && data.poll_id) {
            setView((v) => {
              if (!v.current || v.current.id !== data.poll_id) return v;
              return {
                current: null,
                results: {
                  question: v.current.question,
                  options: v.current.options,
                  counts: data.counts || {},
                  total: data.total ?? 0,
                  existing_choice_id: v.current.existing_choice_id ?? null,
                },
              };
            });
          }
          // 'response' events are host-only — ignored by viewers.
        },
      },
    );
    return () => sub.unsubscribe();
  }, [webinarId]);

  const remaining = usePollCountdown(current?.closes_at);

  const submit = useCallback(async () => {
    if (!current || !selected || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(poll.response_endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
          Accept: 'application/json',
        },
        body: JSON.stringify({ poll_id: current.id, option_id: selected }),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      // Persist the locked-in choice so a re-render shows "Answered".
      setView((v) => (v.current ? { ...v, current: { ...v.current, existing_choice_id: selected } } : v));
    } catch (e) {
      setError('Could not submit your answer. Try again.');
    } finally {
      setSubmitting(false);
    }
  }, [current, selected, submitting, poll?.response_endpoint]);

  if (results) {
    const { question, options, counts, total, existing_choice_id: answeredOptionId } = results;
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Poll closed</p>
        <p className="mt-0.5 text-sm font-semibold text-gray-900">{question}</p>
        <ul className="mt-3 space-y-1.5">
          {options.map((opt) => {
            const c = counts[opt.id] || 0;
            const pct = total > 0 ? Math.round((c / total) * 100) : 0;
            return (
              <li key={opt.id} className="text-sm">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="truncate text-gray-800">
                    {opt.body}
                    {answeredOptionId === opt.id && (
                      <span className="ml-2 text-xs font-semibold text-indigo-700">Your answer</span>
                    )}
                  </span>
                  <span className="text-xs tabular-nums text-gray-500">{c} ({pct}%)</span>
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                  <div className="h-full bg-indigo-500 transition-all" style={{ width: `${pct}%` }} />
                </div>
              </li>
            );
          })}
        </ul>
        <p className="mt-3 text-xs text-gray-500">
          {total} total {total === 1 ? 'response' : 'responses'}.
        </p>
      </div>
    );
  }

  if (!current) return null;

  const hasAnswered = current.existing_choice_id != null;

  return (
    <div className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-4 shadow-sm">
      <div className="flex items-baseline justify-between gap-3">
        {/* Focus target for the attention banner. tabIndex -1 makes it
            programmatically focusable without adding a tab stop. */}
        <p
          ref={anchorRef}
          tabIndex={-1}
          className="text-xs font-semibold uppercase tracking-wide text-indigo-700 outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 rounded"
        >
          Live poll
        </p>
        {remaining != null && (
          <p className="text-xs font-semibold tabular-nums text-indigo-700">{formatCountdown(remaining)}</p>
        )}
      </div>
      <p className="mt-0.5 text-sm font-semibold text-gray-900">{current.question}</p>

      <fieldset className="mt-3 space-y-1.5">
        {current.options.map((opt) => (
          <label
            key={opt.id}
            className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer transition ${
              selected === opt.id
                ? 'border-indigo-400 bg-white'
                : 'border-gray-200 bg-white hover:border-indigo-200'
            }`}
          >
            <input
              type="radio"
              name={`poll-${current.id}`}
              value={opt.id}
              checked={selected === opt.id}
              onChange={() => setSelected(opt.id)}
              className="text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-800">{opt.body}</span>
          </label>
        ))}
      </fieldset>

      {error && (
        <p className="mt-2 text-xs text-rose-600">{error}</p>
      )}

      <div className="mt-3 flex items-center justify-between">
        <p className="text-xs text-gray-500">
          {hasAnswered ? 'Answer locked in — you can change it until the host closes the poll.' : 'Pick one.'}
        </p>
        <button
          type="button"
          onClick={submit}
          disabled={!selected || submitting || selected === current.existing_choice_id}
          className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        >
          {submitting ? 'Submitting…' : hasAnswered ? 'Update answer' : 'Submit answer'}
        </button>
      </div>
    </div>
  );
}

// Sticky attention banner for interactions the student still owes an answer to.
//
// The poll and question cards sit below the slides, the presenter window and the
// status bar, so on a laptop — and certainly on a phone — a poll can open
// entirely off-screen and the student never knows. This is the smallest surface
// that fixes that: one sticky strip at the top of the room column with a button
// per outstanding interaction.
//
// Sticky top rather than fixed bottom on purpose: on narrow screens the
// presenter window is `fixed inset-x-2 bottom-2 z-[60]`, so anything pinned to
// the bottom would sit underneath it.
function InteractionAttentionBanner({ items }) {
  if (!items.length) return null;

  return (
    <div className="sticky top-0 z-50 -mx-4 sm:-mx-6 px-4 sm:px-6 pt-1 pb-2 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80">
      <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 shadow-sm">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <p className="text-sm font-semibold text-amber-900">
            {items.length === 1
              ? `${items[0].label} needs your answer`
              : `${items.length} activities need your answer`}
          </p>
          <div className="flex flex-wrap items-center gap-2 ml-auto">
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={item.onActivate}
                className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-700 focus-visible:ring-offset-2 cursor-pointer"
              >
                {items.length === 1 ? 'Go to it' : `Go to ${item.label.toLowerCase()}`}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Viewer-side free-text question widget. Sibling of PollViewer: renders the
// currently open question with a textarea and a 2-minute countdown; on submit
// POSTs to the response endpoint and lets the viewer revise their answer until
// the countdown elapses or the host closes it. When the timer hits zero the
// input locks; when a close event arrives it swaps to a "closed"
// acknowledgement showing the viewer's own submitted answer (other viewers'
// answers are never sent here — only the host sees the full feed).
function QuestionViewer({ webinarId, question, onAttentionChange, anchorRef }) {
  // One state for the same reason as PollViewer: the close payload carries no
  // prompt, so the closed card can only be built from the question being shown.
  const [view, setView] = useState(() => ({
    current: question?.current || null,
    closed: question?.previous ? {
      prompt: question.previous.prompt,
      answer: question.previous.existing_answer ?? null,
    } : null,
  }));
  const { current, closed } = view;
  const [draft, setDraft] = useState(() => question?.current?.existing_answer || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(null);

  // Same contract as PollViewer — see the comment there. `existing_answer` is
  // only set after a successful POST, so the banner quiets on confirmed
  // submission rather than on optimistic typing.
  const attentionId = current?.id ?? null;
  const attentionAnswered = current?.existing_answer != null;
  useEffect(() => {
    onAttentionChange?.(attentionId ? { id: attentionId, answered: attentionAnswered } : null);
  }, [attentionId, attentionAnswered, onAttentionChange]);
  useEffect(() => () => onAttentionChange?.(null), [onAttentionChange]);

  // Reset the draft when a new question opens via the channel.
  useEffect(() => {
    setDraft(current?.existing_answer || '');
  }, [current?.id]);

  const refreshedQuestion = question?.current;
  useEffect(() => {
    if (!refreshedQuestion?.id || refreshedQuestion.existing_answer == null) return;
    // Ignore a partial reload that belongs to a previously active question.
    if (current?.id !== refreshedQuestion.id || current.existing_answer != null) return;
    setView((v) => (
      v.current && v.current.id === refreshedQuestion.id && v.current.existing_answer == null
        ? { ...v, current: { ...v.current, existing_answer: refreshedQuestion.existing_answer } }
        : v
    ));
    setDraft((d) => (d === '' ? refreshedQuestion.existing_answer : d));
  }, [refreshedQuestion?.id, refreshedQuestion?.existing_answer, current?.id, current?.existing_answer]);

  // Drive the countdown off the server-provided closes_at so every viewer locks
  // at the same wall-clock moment regardless of when they joined.
  useEffect(() => {
    if (!current?.closes_at) {
      setSecondsLeft(null);
      return undefined;
    }
    const deadline = new Date(current.closes_at).getTime();
    const tick = () => setSecondsLeft(Math.max(0, Math.round((deadline - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [current?.id, current?.closes_at]);

  useEffect(() => {
    if (!webinarId) return undefined;
    const consumer = getCableConsumer();
    const sub = consumer.subscriptions.create(
      { channel: 'WebinarQuestionChannel', webinar_id: webinarId },
      {
        received: (data) => {
          if (!data?.event) return;
          if (data.event === 'open' && data.question?.id) {
            setView({
              current: {
                id: data.question.id,
                prompt: data.question.prompt,
                closes_at: data.question.closes_at || null,
                existing_answer: null,
              },
              closed: null,
            });
            setDraft('');
            setError(null);
            // Prior response state is viewer-specific.
            if (data.question.reopened) {
              router.reload({ only: ['question'], preserveScroll: true });
            }
          } else if (data.event === 'close' && data.question_id) {
            setView((v) => {
              if (!v.current || v.current.id !== data.question_id) return v;
              return {
                current: null,
                closed: { prompt: v.current.prompt, answer: v.current.existing_answer || null },
              };
            });
          }
          // 'response' events are host-only — ignored by viewers.
        },
      },
    );
    return () => sub.unsubscribe();
  }, [webinarId]);

  const expired = secondsLeft === 0;

  const submit = useCallback(async () => {
    const answer = draft.trim();
    if (!current || !answer || submitting || secondsLeft === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(question.response_endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
          Accept: 'application/json',
        },
        body: JSON.stringify({ question_id: current.id, answer }),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      // Persist the submitted answer so a re-render shows "Answer sent".
      setView((v) => (v.current ? { ...v, current: { ...v.current, existing_answer: answer } } : v));
    } catch (e) {
      setError('Could not send your answer. Try again.');
    } finally {
      setSubmitting(false);
    }
  }, [current, draft, submitting, secondsLeft, question?.response_endpoint]);

  if (closed) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Question closed</p>
        <p className="mt-0.5 text-sm font-semibold text-gray-900">{closed.prompt}</p>
        {closed.answer ? (
          <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Your answer</p>
            <p className="mt-0.5 whitespace-pre-wrap break-words text-sm text-gray-800">{closed.answer}</p>
          </div>
        ) : (
          <p className="mt-2 text-xs text-gray-500">This question is no longer accepting answers.</p>
        )}
      </div>
    );
  }

  if (!current) return null;

  const hasAnswered = current.existing_answer != null;
  const unchanged = draft.trim() === (current.existing_answer || '');

  return (
    <div className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-4 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        {/* Focus target for the attention banner — see PollViewer. */}
        <p
          ref={anchorRef}
          tabIndex={-1}
          className="text-xs font-semibold uppercase tracking-wide text-indigo-700 outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 rounded"
        >
          Question from the host
        </p>
        {secondsLeft != null && (
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold tabular-nums ${
              expired ? 'bg-rose-100 text-rose-700' : 'bg-indigo-100 text-indigo-700'
            }`}
          >
            {expired ? "Time's up" : `${formatCountdown(secondsLeft)} left`}
          </span>
        )}
      </div>
      <p className="mt-0.5 text-sm font-semibold text-gray-900">{current.prompt}</p>

      <textarea
        rows={3}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        maxLength={2000}
        // A placeholder is not an accessible name — it disappears on input and
        // several screen readers ignore it. This field is a jump target for
        // the attention banner, so it needs a real one.
        aria-label="Your answer"
        placeholder="Type your answer…"
        disabled={submitting || expired}
        className="mt-3 block w-full resize-none rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none disabled:bg-gray-50"
      />

      {error && <p className="mt-2 text-xs text-rose-600">{error}</p>}

      <div className="mt-3 flex items-center justify-between">
        <p className="text-xs text-gray-500">
          {expired
            ? "Time's up — the host is no longer accepting answers."
            : hasAnswered
              ? 'Answer sent — you can revise it until the countdown ends.'
              : 'Only the host sees your answer.'}
        </p>
        <button
          type="button"
          onClick={submit}
          disabled={!draft.trim() || submitting || unchanged || expired}
          className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        >
          {submitting ? 'Sending…' : hasAnswered ? 'Update answer' : 'Send answer'}
        </button>
      </div>
    </div>
  );
}

// ---- Draggable instructor-video floating window ---------------------------
// Local, dependency-free. The student can drag the instructor camera anywhere;
// position persists in localStorage and is clamped so the window can never be
// dragged fully off-screen. This is UI-position state ONLY — it is deliberately
// kept out of the WHEP/HLS effects so dragging never reconnects the stream.
const INSTRUCTOR_VIDEO_POS_KEY = '123ceu:webinar:instructor-video-position';
const FLOAT_W = 320;      // desktop floating width (px)
// Height estimate for the first clamp, before the element is measured. Kept a
// touch BELOW the real height (≈180 video + ~29 header) on purpose: the initial
// clamp then never pulls a saved bottom-flush position upward, and the
// measure-accurate re-clamp (once the window mounts) becomes authoritative — so
// a persisted position is restored exactly rather than drifting a few px.
const FLOAT_H_EST = 200;
const FLOAT_MARGIN = 12;  // min gap from every viewport edge (px)
const NARROW_MAX = 640;   // <= this viewport width uses the fixed bottom layout (no drag)

function clampFloatPos(x, y, w, h) {
  const vw = (typeof window !== 'undefined' && window.innerWidth) || 1024;
  const vh = (typeof window !== 'undefined' && window.innerHeight) || 768;
  const maxX = Math.max(FLOAT_MARGIN, vw - w - FLOAT_MARGIN);
  const maxY = Math.max(FLOAT_MARGIN, vh - h - FLOAT_MARGIN);
  return {
    x: Math.min(Math.max(FLOAT_MARGIN, x), maxX),
    y: Math.min(Math.max(FLOAT_MARGIN, y), maxY),
  };
}

function defaultFloatPos() {
  const vw = (typeof window !== 'undefined' && window.innerWidth) || 1024;
  const vh = (typeof window !== 'undefined' && window.innerHeight) || 768;
  return clampFloatPos(vw - FLOAT_W - FLOAT_MARGIN, vh - FLOAT_H_EST - FLOAT_MARGIN, FLOAT_W, FLOAT_H_EST);
}

function readStoredFloatPos() {
  try {
    const raw = window.localStorage.getItem(INSTRUCTOR_VIDEO_POS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (!p || typeof p.x !== 'number' || typeof p.y !== 'number' || !Number.isFinite(p.x) || !Number.isFinite(p.y)) return null;
    return { x: p.x, y: p.y };
  } catch (_) {
    return null; // localStorage unavailable or malformed → caller uses the default
  }
}

function WebinarRoomBody({ webinar, playback, section: initialSection, chat, poll, attendance, question, attendanceIndicator }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const tokenRef = useRef(playback.token);
  const [state, setState] = useState(webinar.broadcast_state || 'idle');
  const stateRef = useRef(state);
  stateRef.current = state; // mirror for the player effect's long-lived closures
  const [error, setError] = useState(null);
  const [now, setNow] = useState(new Date());
  // True once the <video> element has carried a real video track (instructor
  // camera). Stays true across a reconnect because the last frame keeps
  // nonzero dimensions — that's fine: it just means "this is a video webinar",
  // which is what gates the floating window. Audio-only webinars leave it false.
  const [hasVideo, setHasVideo] = useState(false);

  // WHEP connect/reconnect lifecycle refs (see the player effect). None of
  // these are in the streaming effect's deps, so dragging/UI never reconnects.
  const disconnectTimerRef = useRef(null);   // grace timer for a transient ICE blip
  const reconnectTimerRef = useRef(null);    // bounded-backoff reconnect timer
  const reconnectAttemptRef = useRef(0);     // backoff index
  const connectingRef = useRef(false);       // guard: exactly one WHEP attempt at a time
  const whepEverConnectedRef = useRef(false);// distinguishes "dropped" (retry) from "never worked" (HLS)
  const connectWhepRef = useRef(null);       // lets the broadcast_state channel (re)connect
  const stopHlsRef = useRef(null);           // lets the 'ended' push tear the fallback down
  // First-connect ladder. MediaMTX fires runOnReady the instant the publisher
  // attaches, but its WHEP read path needs a moment longer — so the attempt a
  // waiting student makes the moment `live` arrives often fails. This index
  // walks WHEP_STARTUP_BACKOFF_MS so a single early failure does not strand the
  // student on the audio-only HLS fallback until they refresh.
  const whepStartupAttemptRef = useRef(0);
  // Bumped whenever a ladder is armed or torn down. Every async step inside an
  // attempt re-checks it, so a slow attempt that resolves after a newer one
  // started can neither install its peer connection nor drop the player to HLS.
  const whepGenerationRef = useRef(0);
  const armWhepStartupRef = useRef(null);    // lets the broadcast_state channel arm a fresh ladder
  // Last broadcast_state seen on the wire. Only a genuine transition INTO live
  // re-arms the ladder; a duplicate `live` push must not start a parallel one.
  const lastBroadcastStateRef = useRef(webinar.broadcast_state || 'idle');
  // When the <video> last actually ADVANCED. A feed that dies mid-session
  // freezes on its final frame while readyState/paused/videoWidth all keep
  // reporting a healthy element, so element state alone cannot tell "playing"
  // from "frozen" — elapsed media time can. Stamped on timeupdate + playing.
  const lastProgressRef = useRef({ at: 0, ct: -1 });

  // Playback state includes 'reconnecting' (see the state model). The
  // floating window shows the live video when 'live', a black "Reconnecting…"
  // placeholder when 'reconnecting', and collapses to the 0×0 sink otherwise.
  const showWindow = hasVideo && (state === 'live' || state === 'reconnecting');
  const showVideo = hasVideo && state === 'live';
  const reconnecting = hasVideo && state === 'reconnecting';

  // Floating instructor-video window — drag/position UI state only. None of
  // these are referenced by the WHEP/HLS effects, so dragging the window can
  // never restart the peer connection (see the note on the player effect).
  const floatRef = useRef(null);
  const dragRef = useRef(null);   // in-flight drag: {startX,startY,baseX,baseY,w,h}
  const didDragRef = useRef(false);
  const [dragging, setDragging] = useState(false);
  const [cameraHidden, setCameraHidden] = useState(false);
  const [isNarrow, setIsNarrow] = useState(
    () => (typeof window !== 'undefined' ? window.innerWidth <= NARROW_MAX : false),
  );
  // Hiding is visual only: the wrapper collapses to 0x0 but the <video> below
  // stays mounted with its srcObject, so playback and audio never restart.
  const cameraVisible = showWindow && !(isNarrow && cameraHidden);
  const [videoPos, setVideoPos] = useState(() => {
    const base = readStoredFloatPos() || defaultFloatPos();
    return clampFloatPos(base.x, base.y, FLOAT_W, FLOAT_H_EST);
  });
  const posRef = useRef(videoPos);
  posRef.current = videoPos; // mirror latest position for the persist effect

  // Viewer sees exactly one section at a time — the one the host has active.
  // Real-time updates arrive on WebinarPresentationChannel: `event=page` for
  // deck advances, `event=bio_slide` for the bio toggle, and `event=section`
  // for a switch to a different section (partial Inertia reload — pulls the
  // new section's polls/questions/deck without dropping the audio stream).
  const [section, setSection] = useState(initialSection || null);

  // Adopt the incoming `initialSection` when it points at a different section
  // than we're currently tracking (i.e. the host activated another section and
  // Inertia handed us fresh props). Adopting on every prop-object identity
  // change would clobber channel-derived updates (`page`, `bio_slide`) that
  // arrived after the partial-reload request left the client — Inertia hands
  // back a NEW object even when the section id is unchanged.
  useEffect(() => {
    if (initialSection && initialSection.id !== section?.id) {
      setSection(initialSection);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSection?.id]);

  useEffect(() => {
    if (!webinar.id) return;
    const consumer = getCableConsumer();
    const sub = consumer.subscriptions.create(
      { channel: 'WebinarPresentationChannel', webinar_id: webinar.id },
      {
        received: (data) => {
          if (!data?.event) return;
          if (data.event === 'page' && section && data.current_section_id === section.id && typeof data.page === 'number') {
            setSection((s) => (s ? { ...s, current_slide_page: data.page } : s));
          } else if (data.event === 'bio_slide' && section && data.section_id === section.id) {
            setSection((s) => (s ? { ...s, show_bio_slide: !!data.show_bio_slide } : s));
          } else if (data.event === 'section') {
            // Host activated a different section. Refresh only the props tied
            // to it — the audio <video> element and WHEP session stay mounted,
            // so viewers never leave the broadcast.
            router.reload({ only: ['section', 'poll', 'question'], preserveScroll: true });
          } else if (data.event === 'broadcast_state') {
            // Authoritative state pushed from Rails (MediaMTX ready/not_ready):
            //   live  → a publisher is attached
            //   idle  → no publisher: not started yet, OR interrupted
            //   ended → the instructor explicitly stopped (definitive)
            const previousBroadcastState = lastBroadcastStateRef.current;
            lastBroadcastStateRef.current = data.state;

            if (data.state === 'ended') {
              // Definitive end → close the window and stop all reconnecting.
              // Mirror the ref synchronously, exactly as the live/idle branches
              // do. Without this, whepSessionRef.stop() below closes the peer
              // connection and fires onWhepState('closed') while stateRef still
              // reads 'live' — whose guard then falls through to
              // scheduleReconnect() and re-opens WHEP after Stop Broadcast.
              stateRef.current = 'ended';
              setState('ended');
              // Invalidate any in-flight attempt: a negotiation already awaiting
              // MediaMTX must not install a session after the webinar is over.
              whepGenerationRef.current += 1;
              whepStartupAttemptRef.current = 0;
              if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
              if (disconnectTimerRef.current) { clearTimeout(disconnectTimerRef.current); disconnectTimerRef.current = null; }
              whepSessionRef.current?.stop();
              whepSessionRef.current = null;
              // A viewer who never got WHEP working is sitting on the HLS
              // fallback; without this it stays attached and keeps pulling the
              // (now dead) live playlist after the broadcast has ended.
              stopHlsRef.current?.();
            } else if (data.state === 'live') {
              // Instructor (re)started publishing. Only a genuine transition
              // INTO live arms a fresh first-connect ladder — a duplicate `live`
              // push (MediaMTX can re-announce) must not start a second ladder
              // racing the one already walking.
              const enteringLive = previousBroadcastState !== 'live';
              if (enteringLive && stateRef.current !== 'live') {
                // Clear a prior 'ended' (mirror the ref synchronously) so the
                // connect attempt below isn't blocked by connectWhep's ended
                // guard; show the reconnecting placeholder until frames paint.
                stateRef.current = 'reconnecting';
                setState('reconnecting');
                // Fresh budget of WHEP attempts across MediaMTX's readiness
                // window — this is what stops an early-joining student from
                // being stranded on the audio-only HLS fallback until refresh.
                armWhepStartupRef.current?.();
              }
            } else if (data.state === 'idle') {
              // The publisher went away without an intentional-stop signal, so
              // the server is holding the webinar reconnectable rather than
              // ending it. Only act for a viewer who has actually been live —
              // someone who joined before the first publish stays on the
              // "Waiting to start" path their own retry loop already drives.
              if (!whepEverConnectedRef.current) return;
              // Trust the <video> element over the push ONLY if it is genuinely
              // advancing. This guard exists for the stale-not_ready race (the
              // instructor restarted within a second and the callbacks arrived
              // out of order) — covering a working feed would be a lie.
              //
              // Elapsed media time is the only honest test: readyState, paused
              // and videoWidth all stay satisfied forever when a feed simply
              // stops arriving, because the element freezes on its last frame.
              const v = videoRef.current;
              const advancing = v && !v.paused &&
                (performance.now() - lastProgressRef.current.at) < 2000;
              if (advancing) return;
              reconnectAttemptRef.current = 0;
              stateRef.current = 'reconnecting';
              setState('reconnecting');
              connectWhepRef.current?.();
            }
          }
        },
      },
    );
    return () => sub.unsubscribe();
  }, [webinar.id, section?.id]);

  // Detailed engagement tracking — tab focus, video player events, heartbeat,
  // plus slide_viewed / poll_offered_seen forwarded from the child viewers.
  // No-op if the server didn't ship the endpoint (e.g. external-delivery webinars).
  const { emit: emitAttendance } = useAttendanceEvents({
    endpoint: attendance?.events_endpoint || null,
    videoRef,
    enabled: !!attendance?.events_endpoint,
  });
  const handleSlideChange = useCallback((slide) => {
    emitAttendance('slide_viewed', { slide });
  }, [emitAttendance]);
  const handlePollOffered = useCallback((pollId) => {
    emitAttendance('poll_offered_seen', { poll_id: pollId });
  }, [emitAttendance]);

  // ---- Outstanding-interaction attention -----------------------------------
  //
  // PollViewer and QuestionViewer own their own state (and their own cable
  // subscriptions), so rather than lifting all of it up — a big, risky refactor
  // of working code — each reports just the one fact this banner needs:
  // "interaction <id> is open and still unanswered". Both can be open at once,
  // since the backend tracks current_poll_id and current_question_id
  // independently, so the banner handles one or two entries.
  const [pollAttention, setPollAttention] = useState(null);       // { id, answered } | null
  const [questionAttention, setQuestionAttention] = useState(null);
  const pollAnchorRef = useRef(null);
  const questionAnchorRef = useRef(null);

  // Identity-compared so a duplicate cable payload for the same interaction is
  // not a state change and cannot re-announce or re-render the banner.
  const applyAttention = useCallback((setter) => (next) => {
    setter((prev) => {
      if (prev === next) return prev;
      if (prev && next && prev.id === next.id && prev.answered === next.answered) return prev;
      return next;
    });
  }, []);
  const handlePollAttention = useCallback(applyAttention(setPollAttention), [applyAttention]);
  const handleQuestionAttention = useCallback(applyAttention(setQuestionAttention), [applyAttention]);

  // Bring a card into view and hand it keyboard focus. Only ever runs from the
  // student's own click — an arriving event never steals focus or scrolls the
  // page out from under someone reading or typing in chat.
  const revealInteraction = useCallback((ref) => {
    const el = ref.current;
    if (!el) return;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    // preventScroll: the smooth scroll above owns the movement; letting focus()
    // scroll too causes a visible double-jump.
    el.focus({ preventScroll: true });
  }, []);

  const attentionItems = [];
  if (pollAttention && !pollAttention.answered) {
    attentionItems.push({
      key: `poll-${pollAttention.id}`,
      label: 'Poll',
      onActivate: () => revealInteraction(pollAnchorRef),
    });
  }
  if (questionAttention && !questionAttention.answered) {
    attentionItems.push({
      key: `question-${questionAttention.id}`,
      label: 'Question',
      onActivate: () => revealInteraction(questionAnchorRef),
    });
  }

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);

  // Must resolve rather than reject. Its callers are a timer and hls.js's error
  // handler, neither of which can catch a rejection, so a transport-level
  // failure — sleep/wake, wifi drop, LTE handoff — escaped as an unhandled
  // rejection. Returning null is what both callers already treat as "no token".
  const refreshToken = useCallback(async () => {
    let res;
    try {
      res = await fetch(playback.refresh_endpoint, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
    } catch {
      return null;
    }
    if (res.status === 410) { setState('ended'); return null; }
    if (!res.ok) { setError('Lost access to this webinar.'); return null; }
    let data;
    try {
      data = await res.json();
    } catch {
      return null;
    }
    // A 200 carrying no token — a proxy error page, or a login page reached by
    // following a redirect — must not overwrite a token that still works.
    if (!data?.token) return null;
    tokenRef.current = data.token;
    return data.token;
  }, [playback.refresh_endpoint]);

  // Keep the playback token fresh — refresh at 60% of its TTL.
  useEffect(() => {
    const ttlMs = (playback.token_ttl_seconds || 300) * 1000;
    const id = setInterval(refreshToken, Math.max(ttlMs * 0.6, 30_000));
    return () => clearInterval(id);
  }, [playback.token_ttl_seconds, refreshToken]);

  // Bounded fallback retries: the playlist 404s until the publisher exists, and
  // an unbounded reload chain becomes a sustained index.m3u8 storm.
  const hlsRetryAttemptRef = useRef(0);
  const hlsRetryTimerRef = useRef(null);
  const nativeHlsRef = useRef(false);
  // 'whep' once the WebRTC path connects; 'hls' once the LL-HLS fallback
  // path attaches. The safety-net retry below dispatches per kind.
  const playerKindRef = useRef(null);
  const whepSessionRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const video = videoRef.current;
    if (!video) return;

    // Detach whatever HLS fallback is currently bound to the shared <video>.
    // Both callers below depend on this: startHls so a second fallback attempt
    // can't leak the first still-attached instance, and connectWhep so a
    // fallback started during an earlier failed attempt can't keep fetching
    // segments (and playing its audio) underneath a live WebRTC feed.
    function clearHlsRetryTimer() {
      if (hlsRetryTimerRef.current) {
        clearTimeout(hlsRetryTimerRef.current);
        hlsRetryTimerRef.current = null;
      }
    }

    function stopHls() {
      clearHlsRetryTimer();
      if (hlsRef.current) {
        try { hlsRef.current.destroy(); } catch (_) { /* ignore */ }
        hlsRef.current = null;
      }
      if (nativeHlsRef.current) {
        nativeHlsRef.current = false;
        if (video) { video.removeAttribute('src'); video.load(); }
      }
      if (playerKindRef.current === 'hls') playerKindRef.current = null;
    }

    async function startHls() {
      if (!playback.hls_url) {
        setError('Stream playback is not configured.');
        return;
      }
      // A previous fallback may still be attached (first WHEP attempt failed,
      // then a later one failed too). Never stack two players on one element.
      stopHls();
      hlsRetryAttemptRef.current = 0;
      const initialUrl = appendToken(playback.hls_url, tokenRef.current);
      // Only Safari truly plays HLS via <video src> — Chrome's canPlayType
      // returns "maybe" for HLS but its native pipeline buffers ~3s. Gate
      // on "probably" so non-Safari browsers fall through to hls.js, which
      // we've tuned for low-latency below.
      const canNative = video.canPlayType('application/vnd.apple.mpegurl') === 'probably';
      if (canNative) {
        nativeHlsRef.current = true;
        playerKindRef.current = 'hls';
        video.src = initialUrl;
        return;
      }
      const Hls = await loadHlsJs();
      if (cancelled) return;
      if (!Hls.isSupported()) {
        setError('Your browser does not support live HLS playback.');
        return;
      }
      const hls = new Hls({
        // Use partial-segment requests against the LL-HLS playlist MediaMTX
        // serves (EXT-X-PART, EXT-X-PRELOAD-HINT). Without this flag hls.js
        // waits for full 1s segments and adds several seconds of latency.
        lowLatencyMode: true,
        // Target ~1.5s behind the live edge — about 7 × 200ms parts.
        liveSyncDuration: 1.5,
        // If the player falls more than 5s behind live, snap forward.
        liveMaxLatencyDuration: 5,
        backBufferLength: 10,
        enableWorker: true,
        // MediaMTX issues a cookieCheck 302 + Set-Cookie on the first playlist
        // request and expects the cookie on the redirect. Cross-origin XHRs
        // need withCredentials=true to accept/resend that cookie — otherwise
        // the player loops on the check and viewers see a black screen on
        // first load.
        xhrSetup: (xhr) => { xhr.withCredentials = true; },
      });
      hlsRef.current = hls;
      playerKindRef.current = 'hls';
      hls.attachMedia(video);
      hls.on(Hls.Events.MEDIA_ATTACHED, () => hls.loadSource(initialUrl));
      hls.on(Hls.Events.ERROR, async (_evt, data) => {
        if (!data.fatal) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          // A 401/403 is a viewer-token rejection, not a warm-up hiccup. Retrying
          // can't fix auth and MediaMTX answers 401 with WWW-Authenticate: Basic,
          // which pops Chrome's native credential dialog — so stop rather than
          // loop. Likewise stop once the broadcast has ended.
          const code = data.response?.code;
          if (code === 401 || code === 403 || stateRef.current === 'ended') {
            hls.destroy();
            if (hlsRef.current === hls) hlsRef.current = null;
            return;
          }
          // Otherwise a benign warm-up case:
          //   1. Publisher hasn't gone live yet → 404 on the playlist.
          //   2. Publisher just went live and the LL-HLS muxer is still
          //      warming up (≈1s before the first valid playlist).
          // Refresh the token and retry — but bail if access was lost (refresh
          // returned null: 410 gone / forbidden) or the broadcast ended.
          // Bounded: a student sitting in a room with no publisher would
          // otherwise generate 404s indefinitely. Give the muxer a fair warm-up
          // window, then stop and let the WHEP path own recovery.
          if (hlsRetryAttemptRef.current >= HLS_MAX_NETWORK_RETRIES) {
            // eslint-disable-next-line no-console
            console.warn('[WebinarRoom] HLS playlist unavailable after ' +
                         `${hlsRetryAttemptRef.current} attempts — stopping fallback retries`);
            stopHls();
            return;
          }
          hlsRetryAttemptRef.current += 1;
          const fresh = await refreshToken();
          if (cancelled || fresh == null || stateRef.current === 'ended') return;
          clearHlsRetryTimer();
          hlsRetryTimerRef.current = setTimeout(() => {
            hlsRetryTimerRef.current = null;
            if (cancelled || !hlsRef.current || stateRef.current === 'ended') return;
            hlsRef.current.loadSource(appendToken(playback.hls_url, tokenRef.current));
          }, 1200);
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          hls.recoverMediaError();
        } else {
          setError('Stream playback failed.');
        }
      });
    }

    // Bounded backoff so a dropped publisher (network blip, instructor crash,
    // MediaMTX restart) recovers without a page refresh and without a tight loop.
    const RECONNECT_BACKOFF_MS = [1000, 2000, 5000];

    // First-connect ladder, walked only while WHEP has never succeeded. Spans
    // ~8s, which comfortably covers MediaMTX's read-path readiness lag after
    // runOnReady, then concedes to HLS rather than retrying forever.
    const WHEP_STARTUP_BACKOFF_MS = [0, 1000, 2000, 5000];

    function clearRetryTimer() {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    }

    // Arms a fresh first-connect ladder. Invalidates any in-flight attempt via
    // the generation token so a straggler can't fight the new one.
    function armWhepStartupLadder() {
      if (cancelled || stateRef.current === 'ended') return;
      whepGenerationRef.current += 1;
      whepStartupAttemptRef.current = 0;
      reconnectAttemptRef.current = 0;
      clearRetryTimer();
      connectWhep();
    }

    // Schedules the next first-connect attempt, or concedes to HLS once the
    // ladder is exhausted. Returns false when there was nothing left to try.
    function scheduleStartupRetry(generation) {
      if (cancelled || stateRef.current === 'ended') return true;
      if (generation !== whepGenerationRef.current) return true; // superseded
      const i = whepStartupAttemptRef.current;
      if (i >= WHEP_STARTUP_BACKOFF_MS.length) return false;
      whepStartupAttemptRef.current = i + 1;
      clearRetryTimer();
      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        if (cancelled || stateRef.current === 'ended') return;
        if (generation !== whepGenerationRef.current) return;
        connectWhep();
      }, WHEP_STARTUP_BACKOFF_MS[i]);
      return true;
    }

    function scheduleReconnect() {
      if (cancelled || stateRef.current === 'ended') return;
      if (reconnectTimerRef.current || connectingRef.current) return;
      const i = Math.min(reconnectAttemptRef.current, RECONNECT_BACKOFF_MS.length - 1);
      reconnectAttemptRef.current += 1;
      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        connectWhep();
      }, RECONNECT_BACKOFF_MS[i]);
    }

    function onWhepState(iceState) {
      if (iceState === 'connected' || iceState === 'completed') {
        whepEverConnectedRef.current = true;
        reconnectAttemptRef.current = 0;
        if (disconnectTimerRef.current) { clearTimeout(disconnectTimerRef.current); disconnectTimerRef.current = null; }
        setError(null);
        // Note: we do NOT set 'live' here — onVideoPlaying does, once real frames
        // paint. That keeps the black "Reconnecting…" placeholder up until the
        // new feed actually renders, so no stale/old frame flashes through.
      } else if (iceState === 'failed' || iceState === 'closed') {
        if (cancelled || stateRef.current === 'ended') return;
        if (disconnectTimerRef.current) { clearTimeout(disconnectTimerRef.current); disconnectTimerRef.current = null; }
        setState('reconnecting');
        scheduleReconnect();
      } else if (iceState === 'disconnected') {
        if (cancelled || stateRef.current === 'ended') return;
        // Grace period: a brief ICE blip often recovers to 'connected' on its
        // own — don't tear the session down or flash the placeholder for that.
        if (!disconnectTimerRef.current) {
          disconnectTimerRef.current = setTimeout(() => {
            disconnectTimerRef.current = null;
            if (cancelled || stateRef.current === 'ended') return;
            setState('reconnecting');
            scheduleReconnect();
          }, 3000);
        }
      }
    }

    // Opens exactly one WHEP session, cleanly closing any prior one first.
    async function connectWhep() {
      if (cancelled || stateRef.current === 'ended') return;
      if (connectingRef.current) {
        // Another negotiation is mid-flight — never overlap peer connections.
        // But this trigger cannot just be dropped: the in-flight attempt belongs
        // to a superseded generation (its own catch will bail), so without
        // re-queueing here a live push that lands while the previous attempt is
        // still resolving would leave no pending retry at all — the exact
        // stranded-student symptom, just with different timing.
        if (!whepEverConnectedRef.current) scheduleStartupRetry(whepGenerationRef.current);
        return;
      }
      // Captured for the whole attempt: every await below re-checks it, so an
      // attempt superseded mid-flight can neither install its session nor drag
      // the player down to HLS behind a newer, successful one.
      const generation = whepGenerationRef.current;
      connectingRef.current = true;
      try { await whepSessionRef.current?.stop(); } catch (_) { /* ignore */ }
      whepSessionRef.current = null;
      // Exactly one player may be attached to the shared <video>. Drop any HLS
      // fallback left over from an earlier failed attempt BEFORE negotiating —
      // doing it after startWhip resolves would race ontrack's srcObject
      // assignment, and video.load() would wipe the WebRTC stream we just got.
      // If this negotiation also fails, the catch below starts a fresh one.
      stopHls();
      try {
        const iceServers = await fetchIceServers(playback.ice_credentials_endpoint);
        if (cancelled || stateRef.current === 'ended') return;
        if (generation !== whepGenerationRef.current) return;
        const url = appendToken(playback.whep_url, tokenRef.current);
        const session = await startWhep({ url, video, iceServers, onStateChange: onWhepState });
        if (cancelled || stateRef.current === 'ended' || generation !== whepGenerationRef.current) {
          session.stop();
          return;
        }
        whepSessionRef.current = session;
        playerKindRef.current = 'whep';
        // Negotiation landed — stop walking the startup ladder. ICE failures
        // from here on are handled by onWhepState/scheduleReconnect instead.
        clearRetryTimer();
        whepStartupAttemptRef.current = 0;
      } catch (e) {
        if (cancelled || stateRef.current === 'ended') return;
        if (generation !== whepGenerationRef.current) return; // a newer attempt owns the player
        if (whepEverConnectedRef.current) {
          // A working WHEP feed dropped (publisher gone / MediaMTX blip) — keep
          // reconnecting rather than switching to the HLS fallback path.
          setState('reconnecting');
          scheduleReconnect();
        } else if (scheduleStartupRetry(generation)) {
          // Publisher isn't serving WHEP yet. Expected while waiting for the
          // instructor and during MediaMTX's post-runOnReady readiness window,
          // so stay on the neutral standby/connecting UI rather than surfacing
          // an error the student can do nothing about.
        } else {
          // Ladder exhausted: the network genuinely can't do WHEP → concede to
          // the LL-HLS fallback. NOTE: that fallback is audio-only for VP8, so
          // the presenter's camera will not appear on this path.
          // eslint-disable-next-line no-console
          console.warn('[WebinarRoom] WHEP retries exhausted, falling back to HLS:', e?.message);
          await startHls();
        }
      } finally {
        connectingRef.current = false;
      }
    }
    connectWhepRef.current = connectWhep;
    armWhepStartupRef.current = armWhepStartupLadder;
    stopHlsRef.current = stopHls;

    async function start() {
      try {
        // Don't attempt playback for a broadcast that's already ended — a
        // pointless HLS request there draws MediaMTX's Basic-auth prompt.
        if (cancelled || stateRef.current === 'ended') return;
        if (playback.whep_url) { await connectWhep(); return; }
        await startHls();
      } catch (e) {
        setError(e?.message || 'Failed to start the player.');
      }
    }
    start();

    return () => {
      cancelled = true;
      // Invalidates any negotiation still awaiting MediaMTX, so a late resolve
      // can't touch a torn-down player.
      whepGenerationRef.current += 1;
      whepStartupAttemptRef.current = 0;
      connectWhepRef.current = null;
      armWhepStartupRef.current = null;
      stopHlsRef.current = null;
      if (disconnectTimerRef.current) { clearTimeout(disconnectTimerRef.current); disconnectTimerRef.current = null; }
      if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
      whepSessionRef.current?.stop();
      whepSessionRef.current = null;
      if (hlsRetryTimerRef.current) { clearTimeout(hlsRetryTimerRef.current); hlsRetryTimerRef.current = null; }
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
      if (video) {
        video.srcObject = null;
        video.removeAttribute('src');
        video.load();
      }
      playerKindRef.current = null;
    };
  }, [playback.whep_url, playback.hls_url, playback.ice_credentials_endpoint, refreshToken]);

  // Safety-net retry: nudge the HLS fallback every 3s until the video element
  // actually has frames to render. WHEP has its own ICE retry inside
  // RTCPeerConnection, so we only drive the fallback path — re-running a WHEP
  // negotiation requires tearing down the PC and starting over, which the outer
  // effect already handles via its cleanup + restart.
  //
  // Every gate below reads a ref, never render state: the interval outlives the
  // render that created it, so a stale `state` closure would keep it reloading
  // after the broadcast has moved on.
  //
  // The retry budget is what bounds this loop on the native path (Safari/iOS),
  // which has no hls.js fatal-error handler to call stopHls() and make the
  // interval inert.
  useEffect(() => {
    if (!playback.hls_url) return undefined;
    const id = setInterval(() => {
      if (stateRef.current === 'ended') return;
      // The server's authoritative broadcast state, not our local playback
      // state — which reads 'reconnecting' during a perfectly normal muxer
      // warm-up. There is no playlist to fetch until a publisher is attached,
      // so retrying before then is pure noise against MediaMTX (and each miss
      // is a 401/404 that can raise Basic-auth prompts).
      if (lastBroadcastStateRef.current !== 'live') return;
      if (playerKindRef.current !== 'hls') return;

      const v = videoRef.current;
      if (!v) return;
      if (v.readyState >= 3 && !v.error) return; // HAVE_FUTURE_DATA — playing

      // Terminal: the browser rejected the source itself. Reassigning the same
      // URL cannot fix that, so tear the fallback down rather than spin.
      if (v.error && NATIVE_HLS_FATAL_MEDIA_ERRORS.includes(v.error.code)) {
        stopHlsRef.current?.();
        return;
      }

      // Shared budget with the hls.js fatal-error handler — one retry system,
      // not two. Exhaustion is terminal: stopHls() nulls playerKindRef, so this
      // interval goes inert without needing a flag of its own.
      if (hlsRetryAttemptRef.current >= HLS_MAX_NETWORK_RETRIES) {
        stopHlsRef.current?.();
        return;
      }
      hlsRetryAttemptRef.current += 1;

      const url = appendToken(playback.hls_url, tokenRef.current);
      if (hlsRef.current) {
        hlsRef.current.loadSource(url);
      } else if (nativeHlsRef.current) {
        v.src = url;
        v.load();
        v.play().catch(() => {}); // ignore autoplay rejections
      }
    }, 3000);
    return () => clearInterval(id);
  }, [playback.hls_url]);

  // Trust the <video> element: when it actually starts painting frames, we're
  // live — this is what promotes 'reconnecting' back to 'live' after a WHEP
  // reconnect, so the placeholder stays up until the new feed renders. Guard
  // against a definitive 'ended' (which a stray late 'playing' must not undo).
  const onVideoPlaying = useCallback(() => {
    // Seed the progress stamp here too: the stall watchdog below starts the
    // moment we flip to 'live', and would otherwise judge a feed that hasn't
    // fired its first timeupdate yet as already stalled.
    const v = videoRef.current;
    lastProgressRef.current = { at: performance.now(), ct: v ? v.currentTime : -1 };
    // Frames are painting, so whatever the fallback spent getting here is paid
    // off — a later drop starts from a full budget rather than an exhausted one.
    // (startHls also zeroes this; that covers a genuinely new lifecycle, this
    // covers a long session that recovers on its own.)
    hlsRetryAttemptRef.current = 0;
    if (stateRef.current !== 'ended') setState('live');
  }, []);

  // Elapsed media time is the only reliable "is this feed alive" signal for
  // WHEP (see lastProgressRef). timeupdate fires ~4×/s while frames arrive and
  // simply stops when they don't.
  const onVideoTimeUpdate = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.currentTime !== lastProgressRef.current.ct) {
      lastProgressRef.current = { at: performance.now(), ct: v.currentTime };
    }
  }, []);

  // Dead-media watchdog for the WHEP path.
  //
  // When the instructor's tab dies, this viewer's peer connection stays healthy
  // — it terminates at MediaMTX, not at the instructor — so ICE never fails and
  // no <video> event fires. The element just freezes. Measured locally against
  // MediaMTX 1.19, the server itself needs ~31s to notice the publisher is gone
  // (Pion ICE failure; no configurable timeout exists).
  //
  // Watching elapsed media time detects it in STALL_TIMEOUT_MS regardless of
  // the server hook. A false positive is cheap and self-correcting: we show the
  // reconnecting placeholder and renegotiate once (connectWhep is single-flight),
  // and onPlaying promotes straight back to 'live' as soon as frames resume.
  useEffect(() => {
    if (state !== 'live') return undefined;
    const STALL_TIMEOUT_MS = 6000;
    const id = setInterval(() => {
      if (playerKindRef.current !== 'whep') return; // HLS has its own retry loop
      if (stateRef.current !== 'live') return;
      const v = videoRef.current;
      if (!v || v.paused) return;                   // paused by the viewer, not stalled
      if (performance.now() - lastProgressRef.current.at < STALL_TIMEOUT_MS) return;
      reconnectAttemptRef.current = 0;
      stateRef.current = 'reconnecting';
      setState('reconnecting');
      connectWhepRef.current?.();
    }, 1000);
    return () => clearInterval(id);
  }, [state]);
  // Intentionally not wired to setState('ended'): a live MediaStream fires
  // 'ended' when we tear a session down mid-reconnect, which would wrongly kill
  // the retry. The authoritative end is the broadcast_state channel push / a
  // 410 token refresh — see those paths.
  // Reveal the video box only when the element reports real dimensions. Fires
  // on loadedmetadata/resize for HLS and once WHEP attaches a video track.
  const onVideoMeta = useCallback(() => {
    const v = videoRef.current;
    setHasVideo(!!v && v.videoWidth > 0 && v.videoHeight > 0);
  }, []);

  // Keep the floating window on-screen on viewport resize, and switch to the
  // fixed bottom layout on narrow screens. Global listener, cleaned up on unmount.
  useEffect(() => {
    const onResize = () => {
      setIsNarrow(window.innerWidth <= NARROW_MAX);
      const el = floatRef.current;
      setVideoPos((p) => clampFloatPos(p.x, p.y, el?.offsetWidth || FLOAT_W, el?.offsetHeight || FLOAT_H_EST));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Re-clamp with the window's real measured size once it actually appears
  // (the initial clamp used a height estimate before the element existed).
  useEffect(() => {
    if (!showWindow || isNarrow) return;
    const el = floatRef.current;
    if (!el) return;
    setVideoPos((p) => clampFloatPos(p.x, p.y, el.offsetWidth || FLOAT_W, el.offsetHeight || FLOAT_H_EST));
  }, [showWindow, isNarrow]);

  // Persist the chosen position after a drag ends (skips the initial mount).
  useEffect(() => {
    if (dragging || !didDragRef.current) return;
    try { window.localStorage.setItem(INSTRUCTOR_VIDEO_POS_KEY, JSON.stringify(posRef.current)); } catch (_) { /* ignore */ }
  }, [dragging]);

  const startsAt = webinar.starts_at ? new Date(webinar.starts_at) : null;
  const endsAt   = webinar.ends_at   ? new Date(webinar.ends_at)   : null;
  const beforeStart = startsAt && now < startsAt;

  const live = state === 'live';

  // Drag handlers live on the header handle only (never the video area). Pointer
  // capture routes move/up to the handle even if the pointer leaves it, so no
  // document-level listeners are needed. Dragging only updates videoPos.
  const onHandlePointerDown = (e) => {
    if (isNarrow) return; // fixed bottom layout on narrow screens — no dragging
    const el = floatRef.current;
    dragRef.current = {
      startX: e.clientX, startY: e.clientY,
      baseX: videoPos.x, baseY: videoPos.y,
      w: el?.offsetWidth || FLOAT_W, h: el?.offsetHeight || FLOAT_H_EST,
    };
    didDragRef.current = true;
    setDragging(true);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* ignore */ }
    e.preventDefault();
  };
  const onHandlePointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    setVideoPos(clampFloatPos(d.baseX + (e.clientX - d.startX), d.baseY + (e.clientY - d.startY), d.w, d.h));
  };
  const onHandlePointerUp = (e) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    setDragging(false);
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* ignore */ }
  };

  return (
    <Layout>
      {/* On narrow screens the instructor window is pinned to the bottom of the
          viewport, where it sits on top of the last thing in this column — in
          practice the chat composer. Reserve scroll room underneath so every
          control can still be scrolled clear of it. Desktop drags the window
          freely and needs no reservation. */}
      <div
        data-testid="room-content"
        className={`max-w-3xl mx-auto p-4 sm:p-6 space-y-4 ${cameraVisible && isNarrow ? 'pb-64' : ''}`}
      >
        {attendanceIndicator}

        <InteractionAttentionBanner items={attentionItems} />

        {/* Announced politely for screen-reader users, who cannot see the
            banner appear. Deliberately not role="alert": a poll is worth
            surfacing, not worth interrupting whatever is being read. Text only
            changes when the set of outstanding interactions changes, so a
            duplicate cable event announces nothing. */}
        <p role="status" aria-live="polite" className="sr-only">
          {attentionItems.length === 0
            ? ''
            : attentionItems.length === 1
              ? `${attentionItems[0].label} needs your answer`
              : `${attentionItems.length} activities need your answer`}
        </p>

        <a href="/webinars" className="text-sm text-indigo-600 hover:text-indigo-700">← Back to webinars</a>

        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 truncate">{webinar.title}</h1>
            {webinar.instructor && <p className="text-sm text-gray-500 mt-0.5">Hosted by {webinar.instructor}</p>}
          </div>
          <BroadcastBadge state={state} />
        </div>

        <SlideViewer
          section={section}
          onSlideChange={handleSlideChange}
          isNarrow={isNarrow}
        />

        {/*
          The <video> element is the single playback sink for BOTH audio and
          the instructor camera, over WHEP or HLS. It MUST stay mounted with a
          stable ref/position across the whole session — remounting would drop
          srcObject / the hls.js attachment and force a reconnect — so it is
          always the last child of this one wrapper and is never conditionally
          unmounted. Only the wrapper's styling changes:
            • no camera  → a 0×0 hidden audio sink, in normal flow.
            • camera     → a fixed, draggable floating window over the slides.
          The presentation (SlideViewer above) stays the dominant content; the
          window is position:fixed so it never reflows the deck. `muted` +
          autoPlay start it without a gesture; the purple bar's LiveAudioControls
          still drive this exact element for play/mute/volume.
        */}
        <div
          ref={floatRef}
          data-testid="instructor-camera"
          className={
            !cameraVisible
              // `relative` is load-bearing: the video below stays absolutely
              // positioned, so without a containing block here it escapes the
              // collapsed wrapper and blankets the page, swallowing clicks.
              ? 'relative w-0 h-0 overflow-hidden pointer-events-none'
              : isNarrow
                ? 'fixed inset-x-2 bottom-2 z-[60] overflow-hidden rounded-xl bg-black shadow-lg ring-1 ring-black/10'
                : 'fixed z-[60] overflow-hidden rounded-xl bg-black shadow-lg ring-1 ring-black/10'
          }
          style={cameraVisible && !isNarrow ? { left: `${videoPos.x}px`, top: `${videoPos.y}px`, width: `${FLOAT_W}px` } : undefined}
        >
          {cameraVisible && (
            <div
              onPointerDown={onHandlePointerDown}
              onPointerMove={onHandlePointerMove}
              onPointerUp={onHandlePointerUp}
              onPointerCancel={onHandlePointerUp}
              className={`flex items-center gap-2 px-2.5 py-1.5 bg-gray-900 text-white select-none ${
                isNarrow ? 'cursor-default' : `touch-none ${dragging ? 'cursor-grabbing' : 'cursor-grab'}`
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${showVideo ? 'bg-rose-400' : 'bg-amber-400'}`} />
              <span className="text-[11px] font-semibold uppercase tracking-wide">Instructor</span>
              {showVideo
                ? <span className="ml-auto text-[10px] font-semibold text-rose-300">LIVE</span>
                : <span className="ml-auto text-[10px] font-semibold text-amber-300">RECONNECTING</span>}
              {isNarrow && (
                <button
                  type="button"
                  onClick={() => setCameraHidden(true)}
                  data-testid="camera-hide"
                  aria-label="Hide instructor camera"
                  className="ml-1 -mr-1 min-h-11 min-w-11 inline-flex items-center justify-center rounded-lg text-[11px] font-semibold text-white/90 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white cursor-pointer"
                >
                  Hide
                </button>
              )}
            </div>
          )}
          {/* Body keeps a 16:9 box while the window is shown so the black
              reconnecting placeholder (below) has somewhere to render even
              though the <video> collapses to 0×0 when it's not the live feed. */}
          <div className={cameraVisible ? 'relative aspect-video w-full bg-black' : ''}>
            <video
              key="instructor-feed"
              data-testid="playback-video"
              ref={videoRef}
              playsInline
              autoPlay
              muted
              crossOrigin="use-credentials"
              onPlaying={onVideoPlaying}
              onTimeUpdate={onVideoTimeUpdate}
              onLoadedMetadata={onVideoMeta}
              onResize={onVideoMeta}
              className={showVideo ? 'absolute inset-0 h-full w-full bg-black object-contain' : 'h-0 w-0'}
              aria-hidden={showVideo ? undefined : 'true'}
            />
            {reconnecting && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black text-white/75">
                <span className="text-xs font-medium tracking-wide">Reconnecting</span>
                <span className="flex items-center gap-1" aria-hidden="true">
                  <span className="h-1.5 w-1.5 rounded-full bg-white/70 animate-pulse [animation-delay:0ms]" />
                  <span className="h-1.5 w-1.5 rounded-full bg-white/70 animate-pulse [animation-delay:200ms]" />
                  <span className="h-1.5 w-1.5 rounded-full bg-white/70 animate-pulse [animation-delay:400ms]" />
                </span>
              </div>
            )}
          </div>
        </div>

        <div
          data-testid="playback-status"
          className="rounded-xl bg-gradient-to-br from-indigo-700 to-indigo-900 text-white shadow-md px-3 py-2 flex items-center gap-3"
        >
          <div className="flex items-center gap-2 shrink-0 min-w-0">
            <span className={`w-2 h-2 rounded-full shrink-0 ${live ? 'bg-rose-400 animate-pulse' : 'bg-white/40'}`} />
            <div className="text-[11px] leading-tight min-w-0">
              <p className="font-semibold tracking-wider uppercase text-white/90">
                {live ? 'Live' : state === 'ended' ? 'Ended' : state === 'reconnecting' ? 'Reconnecting' : 'Standby'}
              </p>
              {!live && (
                <p className="text-white/60 normal-case font-normal tracking-normal truncate">
                  {state === 'ended'
                    ? 'Recording soon'
                    : state === 'reconnecting'
                      ? 'Restoring the stream…'
                      : beforeStart
                        ? `Starts ${fmtClock(startsAt)}`
                        : 'Connecting…'}
                </p>
              )}
            </div>
          </div>

          <LiveAudioControls videoRef={videoRef} />
        </div>

        {showWindow && isNarrow && cameraHidden && (
          <button
            type="button"
            onClick={() => setCameraHidden(false)}
            data-testid="camera-show"
            aria-label="Show instructor camera"
            className="w-full min-h-11 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 cursor-pointer"
          >
            Show instructor camera
          </button>
        )}

        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 text-rose-800 px-4 py-3 text-sm">{error}</div>
        )}

        {poll && (
          <PollViewer
            webinarId={webinar.id}
            poll={poll}
            onPollOffered={handlePollOffered}
            onAttentionChange={handlePollAttention}
            anchorRef={pollAnchorRef}
          />
        )}

        {question && (
          <QuestionViewer
            webinarId={webinar.id}
            question={question}
            onAttentionChange={handleQuestionAttention}
            anchorRef={questionAnchorRef}
          />
        )}

        {chat && <ViewerChat webinarId={webinar.id} chat={chat} />}

        <div className="text-xs text-gray-500">
          Attendance is tracked automatically while the stream is live.
        </div>
      </div>
    </Layout>
  );
}

export default function WebinarRoom(props) {
  const recording = props.recording;
  // Server gates this on hls_delivery + active registration. If the prop is
  // missing (legacy route or external delivery) skip the recorder entirely.
  if (!recording?.start_endpoint) {
    return <WebinarRoomBody {...props} />;
  }
  return (
    <AttendanceRecorder
      webinarId={props.webinar.id}
      startEndpoint={recording.start_endpoint}
      initialBroadcastState={props.webinar.broadcast_state}
    >
      {(attendanceIndicator) => <WebinarRoomBody {...props} attendanceIndicator={attendanceIndicator} />}
    </AttendanceRecorder>
  );
}
